/*!

=========================================================
* Vision UI Free React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-free-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)
* Licensed under MIT (https://github.com/creativetimofficial/vision-ui-free-react/blob/master LICENSE.md)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

import { useState, useEffect } from "react";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import LinearProgress from "@mui/material/LinearProgress";

// Vision UI Dashboard React components
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiProgress from "components/VuiProgress";

// React icons
import { BsCheckCircleFill } from "react-icons/bs";
import { IoWallet } from "react-icons/io5";
import { IoTime } from "react-icons/io5";
import { IoSpeedometer } from "react-icons/io5";
import { IoPulse } from "react-icons/io5";

// Mytherra API Service
import mytherraApi from "services/mytherraApi";

// Vision UI Dashboard theme imports
import colors from "assets/theme/base/colors";

function OrdersOverview() {
  const { gradients } = colors;
  
  const [mempoolData, setMempoolData] = useState({
    size: 0,
    bytes: 0,
    usage: 0,
    maxmempool: 300000000, // 300MB default
    mempoolminfee: 0.00001,
  });
  
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMempoolData = async () => {
      try {
        const info = await mytherraApi.getMempoolInfo();
        if (info) {
          setMempoolData(info);
        }
        setLoading(false);
      } catch (error) {
        console.error("Error fetching mempool data:", error);
        setLoading(false);
      }
    };

    // Initial fetch
    fetchMempoolData();

    // Refresh every 15 seconds
    const interval = setInterval(fetchMempoolData, 15000);

    return () => clearInterval(interval);
  }, []);

  // Calculate congestion level
  const getCongestionLevel = () => {
    const usagePercent = (mempoolData.bytes / mempoolData.maxmempool) * 100;
    if (usagePercent < 20) return { level: "Low", color: "success", percentage: usagePercent };
    if (usagePercent < 60) return { level: "Medium", color: "warning", percentage: usagePercent };
    return { level: "High", color: "error", percentage: usagePercent };
  };

  // Estimate confirmation time based on mempool size
  const getEstimatedConfirmationTime = () => {
    if (mempoolData.size < 100) return "~10 min";
    if (mempoolData.size < 500) return "~20 min";
    if (mempoolData.size < 1000) return "~30 min";
    return "~60 min";
  };

  const congestion = getCongestionLevel();

  return (
    <Card className="h-100">
      <VuiBox mb="16px">
        <VuiTypography variant="lg" fontWeight="bold" mb="5px" color="white">
          Mempool & Network Activity
        </VuiTypography>
        <VuiBox mb={2}>
          <VuiBox display="flex" alignItems="center">
            <IoPulse color={colors[congestion.color]?.main || "#00CED1"} size="15px" />
            <VuiTypography variant="button" color="text" fontWeight="medium" ml="5px" mr="2px">
              {loading ? "Loading..." : `${congestion.level} Activity`}
            </VuiTypography>
          </VuiBox>
        </VuiBox>
      </VuiBox>
      
      <VuiBox>
        {/* Pending Transactions */}
        <VuiBox
          mb={3}
          p={2}
          sx={{
            background: "linear-gradient(135deg, rgba(0, 117, 121, 0.2) 0%, rgba(75, 23, 126, 0.2) 100%)",
            borderRadius: "12px",
            border: "1px solid transparent",
            backgroundImage: "linear-gradient(135deg, rgba(0, 117, 121, 0.2) 0%, rgba(75, 23, 126, 0.2) 100%), linear-gradient(135deg, rgba(0, 206, 209, 0.6) 0%, rgba(138, 43, 226, 0.6) 100%)",
            backgroundOrigin: "border-box",
            backgroundClip: "padding-box, border-box",
          }}
        >
          <VuiBox display="flex" alignItems="center" mb={1}>
            <Icon sx={{ color: "#00CED1", fontSize: "20px", mr: 1 }}>
              pending_actions
            </Icon>
            <VuiTypography variant="button" color="text" fontWeight="regular">
              Pending Transactions
            </VuiTypography>
          </VuiBox>
          <VuiTypography variant="h3" color="white" fontWeight="bold">
            {loading ? "..." : mempoolData.size.toLocaleString()}
          </VuiTypography>
          <VuiTypography variant="caption" color="text">
            {loading ? "..." : `${(mempoolData.bytes / 1024 / 1024).toFixed(2)} MB`}
          </VuiTypography>
        </VuiBox>

        {/* Transaction Fees */}
        <VuiBox
          mb={3}
          p={2}
          sx={{
            background: "linear-gradient(135deg, rgba(0, 117, 121, 0.2) 0%, rgba(75, 23, 126, 0.2) 100%)",
            borderRadius: "12px",
            border: "1px solid transparent",
            backgroundImage: "linear-gradient(135deg, rgba(0, 117, 121, 0.2) 0%, rgba(75, 23, 126, 0.2) 100%), linear-gradient(135deg, rgba(0, 206, 209, 0.6) 0%, rgba(138, 43, 226, 0.6) 100%)",
            backgroundOrigin: "border-box",
            backgroundClip: "padding-box, border-box",
          }}
        >
          <VuiBox display="flex" alignItems="center" mb={1}>
            <IoWallet color="#00CED1" size="20px" style={{ marginRight: "8px" }} />
            <VuiTypography variant="button" color="text" fontWeight="regular">
              Min. Transaction Fee
            </VuiTypography>
          </VuiBox>
          <VuiTypography variant="h3" color="white" fontWeight="bold">
            {loading ? "..." : mempoolData.mempoolminfee?.toFixed(8) || "0.00001000"}
          </VuiTypography>
          <VuiTypography variant="caption" color="text">
            MYT per KB
          </VuiTypography>
        </VuiBox>

        {/* Estimated Confirmation Time */}
        <VuiBox
          mb={3}
          p={2}
          sx={{
            background: "linear-gradient(135deg, rgba(0, 117, 121, 0.2) 0%, rgba(75, 23, 126, 0.2) 100%)",
            borderRadius: "12px",
            border: "1px solid transparent",
            backgroundImage: "linear-gradient(135deg, rgba(0, 117, 121, 0.2) 0%, rgba(75, 23, 126, 0.2) 100%), linear-gradient(135deg, rgba(0, 206, 209, 0.6) 0%, rgba(138, 43, 226, 0.6) 100%)",
            backgroundOrigin: "border-box",
            backgroundClip: "padding-box, border-box",
          }}
        >
          <VuiBox display="flex" alignItems="center" mb={1}>
            <IoTime color="#00CED1" size="20px" style={{ marginRight: "8px" }} />
            <VuiTypography variant="button" color="text" fontWeight="regular">
              Est. Confirmation Time
            </VuiTypography>
          </VuiBox>
          <VuiTypography variant="h3" color="white" fontWeight="bold">
            {loading ? "..." : getEstimatedConfirmationTime()}
          </VuiTypography>
          <VuiTypography variant="caption" color="text">
            Next block
          </VuiTypography>
        </VuiBox>

        {/* Network Congestion */}
        <VuiBox
          p={2}
          sx={{
            background: "linear-gradient(135deg, rgba(0, 117, 121, 0.2) 0%, rgba(75, 23, 126, 0.2) 100%)",
            borderRadius: "12px",
            border: "1px solid transparent",
            backgroundImage: "linear-gradient(135deg, rgba(0, 117, 121, 0.2) 0%, rgba(75, 23, 126, 0.2) 100%), linear-gradient(135deg, rgba(0, 206, 209, 0.6) 0%, rgba(138, 43, 226, 0.6) 100%)",
            backgroundOrigin: "border-box",
            backgroundClip: "padding-box, border-box",
          }}
        >
          <VuiBox display="flex" alignItems="center" mb={2}>
            <IoSpeedometer color="#00CED1" size="20px" style={{ marginRight: "8px" }} />
            <VuiTypography variant="button" color="text" fontWeight="regular">
              Network Congestion
            </VuiTypography>
          </VuiBox>
          <VuiBox display="flex" alignItems="center" mb={1}>
            <VuiTypography variant="h4" color="white" fontWeight="bold" mr={2}>
              {loading ? "..." : congestion.level}
            </VuiTypography>
            <VuiTypography variant="button" color={congestion.color}>
              {loading ? "" : `${congestion.percentage.toFixed(1)}%`}
            </VuiTypography>
          </VuiBox>
          <VuiProgress 
            value={loading ? 0 : congestion.percentage} 
            color={congestion.color}
            sx={{ background: "rgba(255,255,255,0.1)" }}
          />
        </VuiBox>
      </VuiBox>
    </Card>
  );
}

export default OrdersOverview;
